import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { IdExpressApplicationLogo } from "@id-express/global-user-navigation";

import { AuthContext } from "../../contexts/AuthContext";
import {
  buildConsentPersonalDataPayload,
  ConsentCountry,
  ConsentDetails,
  DERIVED_PERMISSION_FIELDS,
  fetchConsentDetails,
  fetchConsentMissingFields,
  isDerivedPermission,
  permissionDisplayLabel,
  permissionFieldKey,
  saveConsentPersonalData,
  updatePermissions,
} from "../../applicationPermissions";
import { appendConsentResult } from "./consentNavigation";
import "./Consent.css";

export const effectiveFieldsToCollect = (selectedPermissions: string[], missing: string[]): string[] => {
  const direct = missing.filter((field) => selectedPermissions.includes(field) && !isDerivedPermission(field));
  const derived = selectedPermissions.flatMap((permission) =>
    (DERIVED_PERMISSION_FIELDS[permission] || []).filter((field) => missing.includes(field))
  );
  return Array.from(new Set([...direct, ...derived]));
};

// A request made only of derived permissions (e.g. Nomorrow asking for the
// 18+ flag) is not a "choose which fields to share" decision: there is a
// single fact to confirm and nothing the user could pick. It gets its own
// wording -- no checkboxes, no "fields" -- and the raw inputs it may need
// are framed as "ID.Express needs this to compute the result", never as
// data the application receives.
export const isDerivedOnlyRequest = (requestedPermissions: string[]): boolean =>
  requestedPermissions.length > 0 && requestedPermissions.every(isDerivedPermission);

const Consent: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const { AuthContextState } = useContext(AuthContext);
  const requestToken = searchParams.get("request") || "";
  const [details, setDetails] = useState<ConsentDetails | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [countries, setCountries] = useState<ConsentCountry[]>([]);
  const [residenceCountry, setResidenceCountry] = useState("");
  const [state, setState] = useState<"loading" | "ready" | "saving" | "granted" | "error">("loading");

  useEffect(() => {
    if (!requestToken || !AuthContextState.isAuthenticated) return;
    let active = true;

    const load = async () => {
      try {
        const payload = await fetchConsentDetails(
          requestToken,
          i18n.resolvedLanguage || i18n.language
        );
        const missing = await fetchConsentMissingFields(payload.requestedPermissions);
        if (!active) return;

        setDetails(payload);
        setSelected(payload.requestedPermissions);
        setMissingFields(missing.missingFields);
        setResidenceCountry(missing.residenceCountry);
        setCountries(Array.isArray(payload.countries) ? payload.countries : []);
        setState("ready");
      } catch {
        if (active) setState("error");
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [AuthContextState.isAuthenticated, i18n.language, requestToken]);

  const toggle = (permission: string) => {
    setSelected((current) => current.includes(permission)
      ? current.filter((item) => item !== permission)
      : [...current, permission]);
  };

  const updateFieldValue = (permission: string, value: string) => {
    setFieldValues((current) => ({ ...current, [permission]: value }));
    setFieldErrors((current) => {
      const next = { ...current };
      delete next[permission];
      return next;
    });
  };

  const validateMissingValues = () => {
    const errors: Record<string, string> = {};

    effectiveFieldsToCollect(selected, missingFields)
      .forEach((permission) => {
        const value = String(fieldValues[permission] || "").trim();
        if (!value) {
          errors[permission] = t("personalData.applicationAccess.requiredField", {
            defaultValue: "Fill in this field before granting access.",
          });
        } else if (permission === "data.profile.birthDate" && value > new Date().toISOString().slice(0, 10)) {
          errors[permission] = t("personalData.applicationAccess.futureBirthDate", {
            defaultValue: "Date of birth cannot be in the future.",
          });
        } else if ((permission === "data.profile.placeOfResidence" && !/^[A-Z]{2}(-[A-Z][A-Z0-9]{0,2})?$/.test(value.toUpperCase()))
          || (permission === "data.residence.country" && !/^[A-Z]{2}$/.test(value.toUpperCase()))) {
          errors[permission] = t("personalData.applicationAccess.invalidCountry", {
            defaultValue: "Select a valid country of residence.",
          });
        } else if (permission === "data.residence.region" && (
          !/^[A-Z]{2}-[A-Z][A-Z0-9]{0,2}$/.test(value.toUpperCase())
          || !value.toUpperCase().startsWith(`${String(fieldValues["data.residence.country"] || residenceCountry || value.slice(0, 2)).toUpperCase()}-`)
        )) {
          errors[permission] = t("personalData.applicationAccess.invalidCountry", {
            defaultValue: "Select a valid country of residence.",
          });
        }
      });

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const renderFieldInput = (field: string) => (
    <div className="pd-consent-field__input" key={field}>
      {field === "data.profile.birthDate" ? (
        <input
          aria-label={t("personalData.fields.birthDate", { defaultValue: "Date of birth" })}
          type="date"
          max={new Date().toISOString().slice(0, 10)}
          value={fieldValues[field] || ""}
          onChange={(event) => updateFieldValue(field, event.target.value)}
        />
      ) : (field === "data.profile.placeOfResidence" || field === "data.residence.country") && countries.length > 0 ? (
        <select
          aria-label={t("personalData.fields.placeOfResidence", { defaultValue: "Country of residence" })}
          value={fieldValues[field] || ""}
          onChange={(event) => updateFieldValue(field, event.target.value)}
        >
          <option value="">{t("personalData.applicationAccess.selectCountry", { defaultValue: "Select a country" })}</option>
          {countries.map((country) => (
            <option key={country.code} value={country.code}>
              {country.flag ? `${country.flag} ` : ""}{country.name}
            </option>
          ))}
        </select>
      ) : (
        <input
          aria-label={t(`personalData.fields.${permissionFieldKey(field)}`, { defaultValue: field })}
          type="text"
          maxLength={field === "data.profile.placeOfResidence" || field === "data.residence.country" || field === "data.residence.region" ? 6 : undefined}
          placeholder={field === "data.residence.region" ? "MD-CU" : field === "data.profile.placeOfResidence" || field === "data.residence.country" ? "MD" : undefined}
          value={fieldValues[field] || ""}
          onChange={(event) => updateFieldValue(
            field,
            field === "data.profile.placeOfResidence" || field === "data.residence.country" || field === "data.residence.region"
              ? event.target.value.toUpperCase()
              : event.target.value
          )}
        />
      )}
      {fieldErrors[field] && <small className="pd-consent-field__error" role="alert">{fieldErrors[field]}</small>}
    </div>
  );

  const redirect = (result: "granted" | "denied") => {
    if (details?.returnUrl) {
      window.location.assign(appendConsentResult(details.returnUrl, result));
    }
  };

  const allow = async () => {
    if (!details || !selected.length) return;
    if (!validateMissingValues()) return;
    setState("saving");
    try {
      const selectedMissingFields = effectiveFieldsToCollect(selected, missingFields);
      const personalDataPayload = buildConsentPersonalDataPayload(
        fieldValues, selectedMissingFields, residenceCountry
      );
      if (Object.keys(personalDataPayload).length > 0) {
        await saveConsentPersonalData(personalDataPayload);
      }
      await updatePermissions({ permissions: selected, requestToken });
      if (details.returnUrl) {
        redirect("granted");
        return;
      }
      setState("granted");
    } catch {
      setState("error");
    }
  };

  if (!requestToken) {
    return <section className="pd-consent-card"><p>{t("personalData.applicationAccess.invalidRequest", { defaultValue: "This consent request is missing or invalid." })}</p></section>;
  }

  if (state === "loading") {
    return <section className="pd-consent-card"><p>{t("personalData.applicationAccess.loading", { defaultValue: "Loading application access..." })}</p></section>;
  }

  if (state === "error" || !details) {
    return <section className="pd-consent-card"><p>{t("personalData.applicationAccess.loadError", { defaultValue: "Could not load application access. Please try again." })}</p></section>;
  }

  const derivedOnly = isDerivedOnlyRequest(details.requestedPermissions);

  return (
    <section className="pd-consent-card">
      <header className="pd-consent-card__header">
        <span className="pd-consent-card__logo">
          <IdExpressApplicationLogo
            name={details.application.name}
            src={details.application.urlImage}
            imageClassName="pd-consent-card__logo-image"
            fallbackClassName="pd-consent-card__logo-fallback"
          />
        </span>
        <div>
          <p>{t("personalData.applicationAccess.requestLabel", { defaultValue: "Requests access to your personal data" })}</p>
          <h1>{details.application.name}</h1>
        </div>
      </header>

      {state === "granted" ? (
        <div className="pd-consent-result" role="status">
          <h2>{t("personalData.applicationAccess.grantedTitle", { defaultValue: "Access granted" })}</h2>
          <p>{derivedOnly
            ? t("personalData.applicationAccess.derivedGrantedDescription", { defaultValue: "The application will only receive the confirmation it asked for — none of your personal data fields." })
            : t("personalData.applicationAccess.grantedDescription", { defaultValue: "The application can now receive the selected fields." })}</p>
          <ul>
            {selected.map((permission) => (
              <li key={permission}>✓ {permissionDisplayLabel(permission, t)}</li>
            ))}
          </ul>
          {details.returnUrl && (
            <button type="button" className="pd-primary-button" onClick={() => redirect("granted")}>
              {t("personalData.applicationAccess.continue", { app: details.application.name, defaultValue: "Continue to {{app}}" })}
            </button>
          )}
        </div>
      ) : derivedOnly ? (
        <>
          <div className="pd-consent-fields">
            {details.requestedPermissions.map((permission) => {
              const fieldsForThis = (DERIVED_PERMISSION_FIELDS[permission] || [])
                .filter((field) => missingFields.includes(field));
              return (
                <div className="pd-consent-field" key={permission}>
                  <label className="pd-consent-field__permission">
                    <input
                      type="checkbox"
                      checked={selected.includes(permission)}
                      onChange={() => toggle(permission)}
                    />
                    <span>
                      <strong>{t(`personalData.applicationAccess.derivedRequest.${permissionFieldKey(permission)}`, { app: details.application.name, defaultValue: permission })}</strong>
                      <small>{t(`personalData.applicationAccess.derivedExplanation.${permissionFieldKey(permission)}`, { defaultValue: "Only the result is shared. The data it is computed from stays with ID.Express and is never sent to this application." })}</small>
                    </span>
                  </label>

                  {fieldsForThis.length > 0 && selected.includes(permission) && (
                    <div className="pd-consent-derived__fields">
                      <p className="pd-consent-derived__fields-hint">{t("personalData.applicationAccess.derivedMissingDescription", { defaultValue: "ID.Express needs this to compute the result. It is saved only in your personal data and is never sent to the application." })}</p>
                      {fieldsForThis.map((field) => (
                        <React.Fragment key={field}>
                          <strong>{t(`personalData.fields.${permissionFieldKey(field)}`, { defaultValue: field })}</strong>
                          {renderFieldInput(field)}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <p className="pd-consent-card__hint">{t("personalData.applicationAccess.manageHint", { defaultValue: "You can change or revoke this access at any time in Personal Data → Security." })}</p>
          <div className="pd-consent-card__actions">
            <button type="button" className="pd-secondary-button" onClick={() => redirect("denied")}>
              {t("personalData.applicationAccess.deny", { defaultValue: "Deny" })}
            </button>
            <button
              type="button"
              className="pd-primary-button"
              disabled={!selected.length || state === "saving"}
              onClick={allow}
            >
              {state === "saving"
                ? t("personalData.applicationAccess.saving", { defaultValue: "Saving..." })
                : t("personalData.applicationAccess.confirmDerived", { defaultValue: "Confirm" })}
            </button>
          </div>
        </>
      ) : (
        <>
          <p>{t("personalData.applicationAccess.requestDescription", { defaultValue: "Choose which fields this application may receive." })}</p>
          {missingFields.some((permission) => selected.includes(permission)) && (
            <div className="pd-consent-missing-intro">
              <h2>{t("personalData.applicationAccess.missingDataTitle", { defaultValue: "Complete missing information" })}</h2>
              <p>{t("personalData.applicationAccess.missingDataDescription", { defaultValue: "These values are not saved in your personal data yet. Fill in only the missing fields, then confirm access." })}</p>
            </div>
          )}
          <div className="pd-consent-fields">
            {details.requestedPermissions.map((permission) => {
              const derivedFields = (DERIVED_PERMISSION_FIELDS[permission] || [])
                .filter((field) => missingFields.includes(field));

              return (
                <div className="pd-consent-field" key={permission}>
                  <label className="pd-consent-field__permission">
                    <input
                      type="checkbox"
                      checked={selected.includes(permission)}
                      onChange={() => toggle(permission)}
                    />
                    <span>
                      <strong>{permissionDisplayLabel(permission, t)}</strong>
                      <small>{permission === "data.derived.ageVerification"
                        ? t("personalData.applicationAccess.ageVerificationExplanation", {
                          defaultValue: "Only shares whether you are an adult. Your birth date and country stay with id.express and are never sent to this application.",
                        })
                        : permission === "data.derived.minimumAgeVerification"
                        ? t("personalData.applicationAccess.minimumAgeVerificationExplanation", {
                          defaultValue: "Only shares whether you have reached a fixed age. Your birth date stays with id.express and is never sent to this application.",
                        })
                        : permission.startsWith("data.residence.")
                        ? t("personalData.applicationAccess.requestDescription")
                        : t(`personalData.applicationAccess.purposes.${permissionFieldKey(permission)}`, { defaultValue: "Requested by this application." })}</small>
                    </span>
                  </label>

                  {missingFields.includes(permission) && selected.includes(permission) && renderFieldInput(permission)}

                  {derivedFields.length > 0 && selected.includes(permission) && (
                    <>{derivedFields.map((field) => renderFieldInput(field))}</>
                  )}
                </div>
              );
            })}
          </div>
          <p className="pd-consent-card__hint">{t("personalData.applicationAccess.manageHint", { defaultValue: "You can change or revoke this access at any time in Personal Data → Security." })}</p>
          <div className="pd-consent-card__actions">
            <button type="button" className="pd-secondary-button" onClick={() => redirect("denied")}>
              {t("personalData.applicationAccess.deny", { defaultValue: "Deny" })}
            </button>
            <button
              type="button"
              className="pd-primary-button"
              disabled={!selected.length || state === "saving"}
              onClick={allow}
            >
              {state === "saving"
                ? t("personalData.applicationAccess.saving", { defaultValue: "Saving..." })
                : t("personalData.applicationAccess.allow", { defaultValue: "Allow selected fields" })}
            </button>
          </div>
        </>
      )}
    </section>
  );
};


export default Consent;
